// Account settings on the web (same API as the app): devices & sign-in, two-step, notifications, privacy & blocked
// people, saved addresses, your data (GDPR export) and deleting your account.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Bell, Download, Laptop, Lock, MapPin, ShieldCheck, Trash2, UserX } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full rounded-xl border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary";
const when = (s) => (s ? new Date(s).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "");
const TABS = [
  ["security", "Sign-in & devices", Lock], ["notifications", "Notifications", Bell], ["privacy", "Privacy", ShieldCheck],
  ["addresses", "Addresses", MapPin], ["data", "Your data", Download], ["delete", "Delete account", UserX],
];
const Toggle = ({ label, hint, checked, onChange }) => (
  <label className="flex cursor-pointer items-start justify-between gap-4 py-3">
    <span><span className="block text-sm font-semibold">{label}</span>{hint ? <span className="block text-xs text-muted-foreground">{hint}</span> : null}</span>
    <input type="checkbox" className="mt-1 h-5 w-5" checked={!!checked} onChange={(e) => onChange(e.target.checked)} />
  </label>
);
const Card = ({ title, children }) => <section className="rounded-2xl border p-5"><h2 className="mb-2 font-bold">{title}</h2>{children}</section>;

function Security() {
  const { user, setUser } = useAuth();
  const [s, setS] = useState(null);
  const [pw, setPw] = useState("");
  const [ch, setCh] = useState(null);
  const [code, setCode] = useState("");
  const load = () => api("/auth/sessions").then(setS).catch(() => setS({ items: [] }));
  useEffect(() => { load(); }, []);
  const out = async (d) => { try { await api("/auth/sessions/" + d.id, { method: "DELETE" }); toast.success("Signed out of that device"); load(); } catch (e) { toast.error(e.message || "Couldn't sign out"); } };
  const outAll = async () => { if (!window.confirm("Sign out of every other device?")) return; try { const r = await api("/auth/sessions/revoke-all", { method: "POST" }); toast.success("Signed out of " + (r.signed_out || 0) + " devices"); load(); } catch (e) { toast.error(e.message || "Couldn't sign out"); } };
  const on = !!(user && (user.two_step_enabled || user.two_step));
  const start = async () => { try { setCh(await api("/auth/two-step/start", { method: "POST", body: { password: pw } })); setPw(""); toast.success("We've sent you a code"); } catch (e) { toast.error(e.message || "Couldn't start"); } };
  const confirm = async () => { try { const r = await api("/auth/two-step/confirm", { method: "POST", body: { challenge_token: ch.challenge_token, code } }); if (r.user) setUser(r.user); setCh(null); setCode(""); toast.success("Two-step sign-in is on"); } catch (e) { toast.error(e.message || "That code didn't work"); } };
  const disable = async () => { try { const r = await api("/auth/two-step/disable", { method: "POST", body: { password: pw } }); if (r.user) setUser(r.user); setPw(""); toast.success("Two-step sign-in is off"); } catch (e) { toast.error(e.message || "Couldn't turn it off"); } };
  return (
    <div className="space-y-6">
      <Card title="Two-step sign-in">
        <p className="text-sm text-muted-foreground">{on ? "On — we ask for a code when you sign in on a new device." : "Add a one-time code to sign-ins on new devices."}</p>
        {ch ? (
          <div className="mt-3 flex gap-2">
            <input className={inp} placeholder={"Code sent to " + (ch.email_hint || "your email")} aria-label="Code" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
            <Button onClick={confirm}>Confirm</Button>
          </div>
        ) : (
          <div className="mt-3 flex gap-2">
            <input className={inp} type="password" placeholder="Your password" aria-label="Password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" />
            {on ? <Button variant="outline" onClick={disable} disabled={!pw}>Turn off</Button> : <Button onClick={start} disabled={!pw}>Turn on</Button>}
          </div>
        )}
      </Card>
      <Card title="Where you're signed in">
        {!s ? <Skeleton className="h-24" /> : (
          <ul className="divide-y">
            {(s.items || []).map((d) => (
              <li key={d.id} className="flex items-center gap-3 py-3 text-sm">
                <Laptop className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <div className="min-w-0 flex-1"><p className="font-semibold">{d.device_name || "Device"}{d.current ? " · this device" : ""}</p>
                  <p className="truncate text-xs text-muted-foreground">{[d.platform, d.location, "active " + when(d.last_active_at)].filter(Boolean).join(" · ")}</p></div>
                {!d.current ? <Button size="sm" variant="ghost" onClick={() => out(d)}>Sign out</Button> : null}
              </li>
            ))}
          </ul>
        )}
        <Button variant="outline" className="mt-3" onClick={outAll}>Sign out everywhere else</Button>
      </Card>
    </div>
  );
}

const NOTIF_LABELS = { booking: "Bookings", reminders: "Reminders", pickup: "Pick-ups & deliveries", casting: "Casting", promotions: "Offers from Fable+", ads: "Sponsored suggestions", posts: "Posts from people you follow", system: "Account & security" };
function Notifications() {
  const [p, setP] = useState(null);
  useEffect(() => { api("/notifications/preferences").then(setP).catch(() => setP(null)); }, []);
  const save = async (patch) => { const next = { ...p, ...patch }; setP(next); try { await api("/notifications/preferences", { method: "PATCH", body: patch }); } catch (e) { toast.error(e.message || "Couldn't save"); } };
  if (!p) return <Skeleton className="h-60 rounded-2xl" />;
  const q = p.quiet_hours || {};
  return (
    <div className="space-y-6">
      <Card title="What we tell you about">
        <div className="divide-y">{Object.keys(p.types || {}).map((k) => <Toggle key={k} label={NOTIF_LABELS[k] || k} checked={p.types[k]} onChange={(v) => save({ types: { ...p.types, [k]: v } })} />)}</div>
        <p className="mt-2 text-xs text-muted-foreground">Account & security messages may still be sent when they're needed to keep your account safe.</p>
      </Card>
      <Card title="Quiet hours">
        <Toggle label="Pause notifications overnight" checked={q.enabled} onChange={(v) => save({ quiet_hours: { ...q, enabled: v } })} />
        {q.enabled ? (
          <div className="mt-2 flex items-center gap-2 text-sm">From
            <select className={inp + " w-24"} value={q.start_hour} onChange={(e) => save({ quiet_hours: { ...q, start_hour: Number(e.target.value) } })} aria-label="Quiet hours start">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
            to
            <select className={inp + " w-24"} value={q.end_hour} onChange={(e) => save({ quiet_hours: { ...q, end_hour: Number(e.target.value) } })} aria-label="Quiet hours end">{Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, "0")}:00</option>)}</select>
          </div>
        ) : null}
      </Card>
    </div>
  );
}

function Privacy() {
  const [p, setP] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const load = () => { api("/privacy/settings").then(setP).catch(() => setP(null)); api("/privacy/blocks").then((r) => setBlocks(r || [])).catch(() => setBlocks([])); };
  useEffect(() => { load(); }, []);
  const save = async (patch) => { try { setP(await api("/privacy/settings", { method: "PATCH", body: patch })); } catch (e) { toast.error(e.message || "Couldn't save"); } };
  const unblock = async (b) => { try { await api("/privacy/blocks/" + (b.user_id || b.id), { method: "DELETE" }); load(); } catch (e) { toast.error(e.message || "Couldn't unblock"); } };
  if (!p) return <Skeleton className="h-60 rounded-2xl" />;
  const m = p.messages || {};
  return (
    <div className="space-y-6">
      <Card title="Messages">
        <label className="block py-2 text-sm font-semibold">Who can message you
          <select className={inp + " mt-1"} value={m.who_can_message} onChange={(e) => save({ messages: { ...m, who_can_message: e.target.value } })}>
            <option value="everyone">Everyone</option><option value="booked">Only providers I've booked</option><option value="nobody">Nobody</option>
          </select>
        </label>
        <Toggle label="Read receipts" hint="Let people see when you've read their message" checked={m.read_receipts} onChange={(v) => save({ messages: { ...m, read_receipts: v } })} />
      </Card>
      <Card title="Location & personalisation">
        <label className="block py-2 text-sm font-semibold">Share your location as
          <select className={inp + " mt-1"} value={p.location_mode} onChange={(e) => save({ location_mode: e.target.value })}>
            <option value="precise">Precise</option><option value="approximate">Approximate (about 1 km)</option><option value="off">Off</option>
          </select>
        </label>
        <Toggle label="Help improve recommendations" hint="Use your activity on Fable+ to personalise what you see" checked={p.improve_ai} onChange={(v) => save({ improve_ai: v })} />
        <Toggle label="Hide me from leaderboards" checked={p.hide_from_leaderboards} onChange={(v) => save({ hide_from_leaderboards: v })} />
      </Card>
      <Card title="Blocked people">
        {blocks.length === 0 ? <p className="text-sm text-muted-foreground">You haven't blocked anyone.</p> : (
          <ul className="divide-y">{blocks.map((b) => <li key={b.user_id || b.id} className="flex items-center justify-between py-2 text-sm"><span>{b.name || b.full_name || b.handle || "Member"}</span><Button size="sm" variant="ghost" onClick={() => unblock(b)}>Unblock</Button></li>)}</ul>
        )}
      </Card>
    </div>
  );
}

function Addresses() {
  const [list, setList] = useState(null);
  const [f, setF] = useState({ label: "Home", line1: "", city: "", postcode: "" });
  const load = () => api("/addresses").then(setList).catch(() => setList([]));
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); if (!f.line1.trim() || !f.postcode.trim()) { toast.error("Add the first line and postcode"); return; } try { await api("/addresses", { method: "POST", body: f }); setF({ label: "Home", line1: "", city: "", postcode: "" }); load(); } catch (err) { toast.error(err.message || "Couldn't save"); } };
  const def = async (a) => { try { await api("/addresses/" + a.id + "/default", { method: "POST" }); load(); } catch (err) { toast.error(err.message || "Couldn't update"); } };
  const del = async (a) => { try { await api("/addresses/" + a.id, { method: "DELETE" }); load(); } catch (err) { toast.error(err.message || "Couldn't remove"); } };
  if (!list) return <Skeleton className="h-40 rounded-2xl" />;
  return (
    <div className="space-y-6">
      <Card title="Saved addresses">
        {list.length === 0 ? <p className="text-sm text-muted-foreground">No saved addresses.</p> : (
          <ul className="divide-y">{list.map((a) => (
            <li key={a.id} className="flex items-center gap-3 py-3 text-sm">
              <div className="min-w-0 flex-1"><p className="font-semibold">{a.label}{a.is_default ? " · default" : ""}</p><p className="truncate text-xs text-muted-foreground">{a.formatted || [a.line1, a.city, a.postcode].filter(Boolean).join(", ")}</p></div>
              {!a.is_default ? <Button size="sm" variant="ghost" onClick={() => def(a)}>Make default</Button> : null}
              <Button size="icon" variant="ghost" aria-label={"Delete " + a.label} onClick={() => del(a)}><Trash2 className="h-4 w-4" /></Button>
            </li>
          ))}</ul>
        )}
      </Card>
      <form onSubmit={add} className="grid gap-3 rounded-2xl border p-5 sm:grid-cols-2" aria-label="Add an address">
        <input className={inp} placeholder="Label (Home, Work…)" aria-label="Label" value={f.label} onChange={(e) => setF({ ...f, label: e.target.value })} />
        <input className={inp} placeholder="First line" aria-label="First line" value={f.line1} onChange={(e) => setF({ ...f, line1: e.target.value })} autoComplete="address-line1" />
        <input className={inp} placeholder="Town or city" aria-label="Town or city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} autoComplete="address-level2" />
        <input className={inp} placeholder="Postcode" aria-label="Postcode" value={f.postcode} onChange={(e) => setF({ ...f, postcode: e.target.value })} autoComplete="postal-code" />
        <Button type="submit" className="sm:col-span-2 sm:justify-self-end">Save address</Button>
      </form>
    </div>
  );
}

function YourData() {
  const [rows, setRows] = useState(null);
  const load = () => api("/privacy/data-requests").then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const req = async () => { try { await api("/privacy/data-requests", { method: "POST" }); toast.success("Your export is being prepared"); load(); } catch (e) { toast.error(e.message || "Couldn't request"); } };
  const base = process.env.REACT_APP_BACKEND_URL + "/api/privacy/data-requests/";
  return (
    <Card title="Download your data">
      <p className="text-sm text-muted-foreground">Everything we hold about you, as JSON or CSV. Links last 7 days (UK GDPR right of access).</p>
      <Button className="mt-3" onClick={req}>Request a copy</Button>
      {!rows ? <Skeleton className="mt-4 h-16" /> : (
        <ul className="mt-4 divide-y">{rows.map((r) => (
          <li key={r.id} className="flex items-center gap-3 py-2 text-sm">
            <span className="flex-1">{when(r.requested_at)} · {r.status}{r.record_count ? " · " + r.record_count + " records" : ""}</span>
            {r.status === "ready" ? (r.formats || ["json"]).map((fmt) => <a key={fmt} className="text-xs font-semibold underline" href={base + r.id + "/download?format=" + fmt}>{fmt.toUpperCase()}</a>) : null}
          </li>
        ))}</ul>
      )}
    </Card>
  );
}

function DeleteAccount() {
  const nav = useNavigate();
  const { logout } = useAuth();
  const [d, setD] = useState(null);
  const [pw, setPw] = useState("");
  const [typed, setTyped] = useState("");
  useEffect(() => { api("/privacy/delete-account").then(setD).catch(() => setD(null)); }, []);
  const go = async () => {
    try { const r = await api("/privacy/delete-account", { method: "POST", body: { password: pw || undefined, confirm: typed } }); toast.success("Your account will be deleted on " + new Date(r.scheduled_at).toLocaleDateString("en-GB") + ". Sign back in before then to cancel."); await logout(); nav("/"); }
    catch (e) { toast.error(e.message || "Couldn't schedule deletion"); }
  };
  if (!d) return <Skeleton className="h-60 rounded-2xl" />;
  if (d.scheduled_at) return <Card title="Deletion scheduled"><p className="text-sm">Your account will be deleted on {new Date(d.scheduled_at).toLocaleDateString("en-GB")}. Signing in again before then cancels it.</p></Card>;
  return (
    <Card title="Delete your account">
      <p className="text-sm text-muted-foreground">You'll have {d.grace_days} days to change your mind.</p>
      {(d.blockers || []).length ? <ul className="mt-3 list-disc pl-5 text-sm text-rose-700">{d.blockers.map((b, i) => <li key={i}>{typeof b === "string" ? b : b.message || b.label}</li>)}</ul> : null}
      <p className="mt-4 text-sm font-semibold">We'll delete</p><ul className="mt-1 list-disc pl-5 text-sm">{(d.will_delete || []).map((x) => <li key={x}>{x}</li>)}</ul>
      <p className="mt-4 text-sm font-semibold">We have to keep</p><ul className="mt-1 list-disc pl-5 text-sm text-muted-foreground">{(d.will_retain || []).map((x) => <li key={x}>{x}</li>)}</ul>
      <div className="mt-5 grid gap-2 sm:grid-cols-2">
        {d.needs_password ? <input className={inp} type="password" placeholder="Your password" aria-label="Password" value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="current-password" /> : null}
        <input className={inp} placeholder='Type "DELETE" to confirm' aria-label="Type DELETE to confirm" value={typed} onChange={(e) => setTyped(e.target.value)} />
      </div>
      <Button variant="destructive" className="mt-3" disabled={typed !== "DELETE" || (d.needs_password && !pw) || (d.blockers || []).length > 0} onClick={go}>Delete my account</Button>
    </Card>
  );
}

export default function AccountSettings() {
  const [tab, setTab] = useState(() => (window.location.hash || "").replace("#", "") || "security");
  useEffect(() => { document.title = "Settings · Fable+"; }, []);
  const pick = (k) => { setTab(k); window.history.replaceState(null, "", "#" + k); };
  const Body = { security: Security, notifications: Notifications, privacy: Privacy, addresses: Addresses, data: YourData, delete: DeleteAccount }[tab] || Security;
  return (
    <main className="mx-auto max-w-5xl px-4 py-8 sm:px-6" data-testid="account-settings">
      <h1 className="text-3xl font-bold tracking-tight">Settings</h1>
      <div className="mt-6 grid gap-6 md:grid-cols-[220px_minmax(0,1fr)]">
        <nav aria-label="Settings sections" className="flex gap-1 overflow-x-auto md:flex-col">
          {TABS.map(([k, label, Icon]) => (
            <button key={k} type="button" onClick={() => pick(k)} aria-current={tab === k ? "page" : undefined}
              className={"flex shrink-0 items-center gap-2 rounded-xl px-3 py-2 text-left text-sm font-semibold transition " + (tab === k ? "bg-primary text-primary-foreground" : "hover:bg-muted")}>
              <Icon className="h-4 w-4" aria-hidden="true" />{label}
            </button>
          ))}
        </nav>
        <div><Body /></div>
      </div>
    </main>
  );
}
